//import {parsePhoneNumber,parsePhoneNumberFromString,format,getExampleNumber,CountryCode,PhoneNumber, Examples } from 'libphonenumber-js'
import {Component,ElementRef,EventEmitter,forwardRef,Input,OnChanges,OnInit,Output,SimpleChanges,ViewChild,} from '@angular/core';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { setTheme } from 'ngx-bootstrap/utils';

import { CountryCodeLib } from './data/country-code';
import { CountryISO } from './enums/country-iso.enum';
import { SearchCountryField } from './enums/search-country-field.enum';
import { ChangeData } from './interfaces/change-data';
import { Country } from './model/country.model';
import { phoneNumberValidator } from './ngx-intl-tel-input.validator';
import { PhoneNumberFormat } from './enums/phone-number-format.enum';
import { LazyService } from '../../../services/lazy.service';

@Component({
    // tslint:disable-next-line: component-selector
    selector: 'dhs-ngx-intl-tel-input',
    templateUrl: './ngx-intl-tel-input.component.html',
    styleUrls: ['./ngx-intl-tel-input.component.css'],
    providers: [
        CountryCodeLib,
        {
            provide: NG_VALUE_ACCESSOR,
            // tslint:disable-next-line:no-forward-ref
            useExisting: forwardRef(() => NgxIntlTelInputComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useValue: phoneNumberValidator,
            multi: true,
        },
    ],
    standalone: false
})
export class NgxIntlTelInputComponent implements OnInit, OnChanges {
	@Input() value: string | undefined = '';
	@Input() preferredCountries: Array<string> = [];
	@Input() enablePlaceholder = true;
	@Input() customPlaceholder: string;
	@Input() numberFormat: PhoneNumberFormat = PhoneNumberFormat.International;
	@Input() cssClass = 'form-control';
	@Input() onlyCountries: Array<string> = [];
	@Input() enableAutoCountrySelect = true;
	@Input() searchCountryFlag = false;
	@Input() searchCountryField: SearchCountryField[] = [SearchCountryField.All];
	@Input() searchCountryPlaceholder = 'Search Country';
	@Input() maxLength: number;
	@Input() selectFirstCountry = true;
	@Input() selectedCountryISO: CountryISO;
	@Input() phoneValidation = true;
	@Input() inputId = 'phone';
	@Input() separateDialCode = false;
	separateDialCodeClass: string;

	@Output() readonly countryChange = new EventEmitter<Country>();

	selectedCountry: Country = {
		areaCodes: undefined,
		dialCode: '',
		htmlId: '',
		flagClass: '',
		iso2: '',
		name: '',
		placeHolder: '',
		priority: 0,
	};

	phoneNumber: string | undefined = '';
	allCountries: Array<Country> = [];
	preferredCountriesInDropDown: Array<Country> = [];
	// Has to be 'any' to prevent a need to install @types/google-libphonenumber by the package user...
	// phoneUtil: any = lpn.PhoneNumberUtil.getInstance();
	// phoneUtil: PhoneNumberUtil = PhoneNumberUtil.getInstance();
	disabled = false;
	errors: Array<any> = ['Phone number is required.'];
	countrySearchText = '';
	
	@ViewChild('countryList') countryList: ElementRef;

	onTouched = () => {};
	propagateChange = (_: ChangeData) => {};
    libPhoneNumber: any;
     
	constructor(
		private countryCodeData: CountryCodeLib,
		private lazyService: LazyService
	) {	
	// If this is not set, ngx-bootstrap will try to use the bs3 CSS (which is not what we've embedded) and will
		// Add the wrong classes and such
		// setTheme('bs4');
	}
	async loadPhoneLib() {
		if (!this.libPhoneNumber) {
		  this.libPhoneNumber = await this.lazyService.loadLibphonenumber();
		}
	  }
	async ngOnInit() {		
			await this.loadPhoneLib();
		  this.init();
	}
	ngOnChanges(changes: SimpleChanges) {
		const selectedISO = changes['selectedCountryISO'];
		if (
			this.allCountries &&
			selectedISO &&
			selectedISO.currentValue !== selectedISO.previousValue
		) {
			this.updateSelectedCountry();
		}
		if (changes['preferredCountries']) {
			this.updatePreferredCountries();
		}
		this.checkSeparateDialCodeStyle();
	}
	
	init() {
		this.fetchCountryData();
		if (this.preferredCountries.length) {
			this.updatePreferredCountries();
		}
		if (this.onlyCountries.length) {
			this.allCountries = this.allCountries.filter((c) =>
				this.onlyCountries.includes(c.iso2)
			);
		}
		if (this.selectFirstCountry) {
			if (this.preferredCountriesInDropDown.length) {
				this.setSelectedCountry(this.preferredCountriesInDropDown[0]);
			} else {
				this.setSelectedCountry(this.allCountries[0]);
			}
		}
		this.updateSelectedCountry();
		this.checkSeparateDialCodeStyle();
	}

	setSelectedCountry(country: Country) {
		this.selectedCountry = country;
		this.countryChange.emit(country);
	}

	/**
	 * Search country based on country name, iso2, dialCode or all of them.
	 */
	public searchCountry() {
		if (!this.countrySearchText) {
			this.countryList.nativeElement
				.querySelector('.iti__country-list li')
				.scrollIntoView({
					behavior: 'smooth',
					block: 'nearest',
					inline: 'nearest',
				});
			return;
		}
		const countrySearchTextLower = this.countrySearchText.toLowerCase();
    // @ts-ignore
		const country = this.allCountries.filter((c) => {
			if (this.searchCountryField.indexOf(SearchCountryField.All) > -1) {
				// Search in all fields
				if (c.iso2.toLowerCase().startsWith(countrySearchTextLower)) {
					return c;
				}
				if (c.name.toLowerCase().startsWith(countrySearchTextLower)) {
					return c;
				}
				if (c.dialCode.startsWith(this.countrySearchText)) {
					return c;
				}
			} else {
				// Or search by specific SearchCountryField(s)
				if (this.searchCountryField.indexOf(SearchCountryField.Iso2) > -1) {
					if (c.iso2.toLowerCase().startsWith(countrySearchTextLower)) {
						return c;
					}
				}
				if (this.searchCountryField.indexOf(SearchCountryField.Name) > -1) {
					if (c.name.toLowerCase().startsWith(countrySearchTextLower)) {
						return c;
					}
				}
				if (this.searchCountryField.indexOf(SearchCountryField.DialCode) > -1) {
					if (c.dialCode.startsWith(this.countrySearchText)) {
						return c;
					}
				}
			}
		});

		if (country.length > 0) {
			const el = this.countryList.nativeElement.querySelector(
				'#' + country[0].htmlId
			);
			if (el) {
				el.scrollIntoView({
					behavior: 'smooth',
					block: 'nearest',
					inline: 'nearest',
				});
			}
		}

		this.checkSeparateDialCodeStyle();
	}

	public onPhoneNumberChange(): void {
		let countryCode: string | undefined;
		if (!this.libPhoneNumber) {
			return;
		  }
		// Handle the case where the user sets the value programatically based on a persisted ChangeData obj.
		if (this.phoneNumber && typeof this.phoneNumber === 'object') {
			const numberObj: ChangeData = this.phoneNumber;
			this.phoneNumber = numberObj.number;
			countryCode = numberObj.countryCode;
		}
		let number:any;
		this.value = this.phoneNumber;
		countryCode = countryCode || this.selectedCountry.iso2;
		if (this.phoneNumber && this.phoneNumber.length >= 5) {
			// @ts-ignore
			number = this.libPhoneNumber.parsePhoneNumber(this.phoneNumber, countryCode.toUpperCase());
		}
		// auto select country based on the extension (and areaCode if needed) (e.g select Canada if number starts with +1 416)
		if (this.enableAutoCountrySelect) {
     		countryCode = number && number.country
          			// @ts-ignore
					? number.country : this.selectedCountry.iso2;
			if (countryCode && countryCode !== this.selectedCountry.iso2) {
				const newCountry = this.allCountries
					.sort((a, b) => {
						return a.priority - b.priority;
					})
					.find((c) => c.iso2 === countryCode);
				if (newCountry) {
					this.selectedCountry = newCountry;
				}
			}
		}
		countryCode = countryCode ? countryCode : this.selectedCountry.iso2;

		this.checkSeparateDialCodeStyle();

		if (!this.value) {
			// @ts-ignore
      this.propagateChange(null);
		} else {
			const intlNo = number
				? number.formatInternational()
				: '';

			// parse phoneNumber if separate dial code is needed
			if (this.separateDialCode && intlNo) {
				const parsedPhoneNumber =this.libPhoneNumber. parsePhoneNumberFromString(intlNo);
				if (parsedPhoneNumber) {
					// Extract the national number without the dial code
					this.value = parsedPhoneNumber.nationalNumber;
					console.log('Phone number without dial code:', this.value);
				} else {
					console.log('Invalid phone number');
				}
			}
			this.propagateChange({
				number: this.value,
				internationalNumber: intlNo,
				nationalNumber: number? number.formatNational(): '',
				e164Number: number ? number.format('E.164'): '',
				countryCode: countryCode.toUpperCase(),
				dialCode: '+' + this.selectedCountry.dialCode,
			});
		}
	}

	public onCountrySelect(country: Country, el: { focus: () => void; }): void {
		this.setSelectedCountry(country);
		this.checkSeparateDialCodeStyle();
		if (this.phoneNumber && this.phoneNumber.length > 0) {
			this.value = this.phoneNumber;
			let countryCode: string | undefined;
			countryCode = this.selectedCountry.iso2;
			let number:any;
			if (this.phoneNumber && this.phoneNumber.length >= 5) {
				// @ts-ignore
				number =  this.libPhoneNumber.parsePhoneNumber(this.phoneNumber, countryCode.toUpperCase());
			}
			
			const intlNo = number
				? number.formatInternational()
				: '';
			// parse phoneNumber if separate dial code is needed
			if (this.separateDialCode && intlNo) {
				const parsedPhoneNumber =this.libPhoneNumber.parsePhoneNumberFromString(intlNo);
				if (parsedPhoneNumber) {
					// Extract the national number without the dial code
					this.value = parsedPhoneNumber.nationalNumber;
					console.log('Phone number without dial code:', this.value);
				} else {
					console.log('Invalid phone number');
				}
				// this.value = this.removeDialCode(intlNo);
			}

			this.propagateChange({
				number: this.value,
				internationalNumber: intlNo,
				nationalNumber: number? number.formatNational(): '',
				e164Number: number? number.format('E.164'): '',
				countryCode: this.selectedCountry.iso2.toUpperCase(),
				dialCode: '+' + this.selectedCountry.dialCode,
			});
		} else {
      		this.propagateChange(null);
		}
		el.focus();
	}

	public onInputKeyPress(event: KeyboardEvent): void {
		const allowedChars = /[0-9\+\-\(\)\ ]/;
		const allowedCtrlChars = /[axcv]/; // Allows copy-pasting
		const allowedOtherKeys = [
			'ArrowLeft',
			'ArrowUp',
			'ArrowRight',
			'ArrowDown',
			'Home',
			'End',
			'Insert',
			'Delete',
			'Backspace',
		];

		if (
			!allowedChars.test(event.key) &&
			!(event.ctrlKey && allowedCtrlChars.test(event.key)) &&
			!allowedOtherKeys.includes(event.key)
		) {
			event.preventDefault();
		}
	}

	registerOnChange(fn: any): void {
		this.propagateChange = fn;
	}

	registerOnTouched(fn: any) {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
	}

	writeValue(obj: any): void {
		if (obj === undefined) {
			this.init();
		}
		this.phoneNumber = obj;
		setTimeout(() => {
			this.onPhoneNumberChange();
		}, 1);
	}

	resolvePlaceholder(): string {
		let placeholder = '';
		//const libPhone = (window as any).libphonenumber;
		if (this.customPlaceholder) {
			placeholder = this.customPlaceholder;
		} else if (this.selectedCountry.placeHolder) {
			placeholder = this.selectedCountry.placeHolder;
			if (this.separateDialCode) {
				const parsedPhoneNumber = this.libPhoneNumber.parsePhoneNumberFromString(placeholder);
				if (parsedPhoneNumber) {
					// Extract the national number without the dial code
					placeholder = parsedPhoneNumber.nationalNumber;
					console.log('Phone number without dial code:', placeholder);
				} else {
					console.log('Invalid phone number');
				}
				// placeholder = this.removeDialCode(placeholder);
			}
		}
		return placeholder;
	}

	private checkSeparateDialCodeStyle() {
		if (this.separateDialCode && this.selectedCountry) {
			const cntryCd = this.selectedCountry.dialCode;
			this.separateDialCodeClass =
				'separate-dial-code iti-sdc-' + (cntryCd.length + 1);
		} else {
			this.separateDialCodeClass = '';
		}
	}

	/**
	 * Cleans dialcode from phone number string.
	 * @param phoneNumber string
	 */
	// private removeDialCode(phoneNumber: string): string {
	// 			let countryCode: string | undefined;
	// 			countryCode = this.selectedCountry.iso2;
	// 			const number = parsePhoneNumber(this.phoneNumber, countryCode);
	// 		// const number = this.getParsedNumber(parsedPhoneNumber);
	// 		if (number) {
	// 			// Do something with the parsed phone number, e.g., format it, extract info, etc.
	// 			phoneNumber = number.format[this.numberFormat]
	// 			if (phoneNumber.startsWith('+') && this.separateDialCode) {
	// 				phoneNumber = phoneNumber.substr(phoneNumber.indexOf(' ') + 1);
	// 			}
	// 		} else {
	// 			console.log('Invalid phone number');
	// 		}
		
	// 	return phoneNumber;
	// }

	

	/**
	 * Gets formatted example phone number from phoneUtil.
	 * @param countryCode string
	 */

	// protected getPhoneNumberPlaceHolder(countryCode: string): string {
	// 	try {
	// 		// let number:any;
	// 		// if (this.phoneNumber && this.phoneNumber.length >= 5) {
	// 		// 	// @ts-ignore
	// 		// 	number = parsePhoneNumber(this.phoneNumber, countryCode.toUpperCase());
	// 		// }
	// 		// Generate an example number for the default country
			
	// 		const exampleNumber = getExampleNumber(countryCode.toUpperCase());
	// 		// let exampleNumber = getExampleNumber(countryCode as CountryCode, Examples.FIXED_LINE);
	// 		// @ts-ignore
	// 		const formattedExampleNumber = format(exampleNumber, 'International');
	// 		// let formattedExampleNumber  = format(exampleNumber, 'International');
	// 		return formattedExampleNumber;
	// 		// return format(
	// 		// 	// @ts-ignore
	// 		// 	getExampleNumber(countryCode), 123456778);
	// 	} catch (e) {
	// 		// @ts-ignore
    //   return e;
	// 	}
	// }

	// protected getPhoneNumberPlaceHolder(countryCode: CountryCode): string {
	// 	try {
	// 		// let countryCode: countryCode;
	// 		let number:any;
	// 		if (this.phoneNumber && this.phoneNumber.length >= 5) {
	// 			// @ts-ignore
	// 			const phoneNumberObject = parsePhoneNumberFromString(this.phoneNumber, countryCode);
    //         if (phoneNumberObject) {
    //             number = phoneNumberObject;
    //         }
    //     }

    //      // Get an example phone number for the specified country code and example type
	// 	 const exampleNumber = getExampleNumber(countryCode as CountryCode, 123456789);

	// 	 // Format the example number based on the number provided
	// 	 const formattedExampleNumber = number ? format(exampleNumber, 'International') : '';
 
	// 	 return formattedExampleNumber;
	// 	} catch (e) {
	// 		// @ts-ignore
    //   return e;
	// 	}
	// }


	/**
	 * Clearing the list to avoid duplicates (https://github.com/webcat12345/ngx-intl-tel-input/issues/248)
	 */
	protected fetchCountryData(): void {
		this.allCountries = [];

		this.countryCodeData.allCountries.forEach((c) => {
			const country: Country = {
				name: c[0].toString(),
				iso2: c[1].toString(),
				dialCode: c[2].toString(),
				priority: +c[3] || 0,
				areaCodes: (c[4] as string[]) || undefined,
				htmlId: `iti-0__item-${c[1].toString()}`,
				flagClass: `iti__${c[1].toString().toLocaleLowerCase()}`,
				placeHolder: '',
			};

			// if (this.enablePlaceholder) {
			// 	country.placeHolder = this.getPhoneNumberPlaceHolder(
			// 		country.iso2.toUpperCase()
			// 	);
			// }

			this.allCountries.push(country);
		});
	}

	/**
	 * Populates preferredCountriesInDropDown with prefferred countries.
	 */
	private updatePreferredCountries() {
		if (this.preferredCountries.length) {
			this.preferredCountriesInDropDown = [];
			this.preferredCountries.forEach((iso2) => {
				const preferredCountry = this.allCountries.filter((c) => {
					return c.iso2 === iso2;
				});

				this.preferredCountriesInDropDown.push(preferredCountry[0]);
			});
		}
	}

	/**
	 * Updates selectedCountry.
	 */
	private updateSelectedCountry() {
		if (this.selectedCountryISO) {
			// @ts-ignore
      this.selectedCountry = this.allCountries.find((c) => {
				return c.iso2.toLowerCase() === this.selectedCountryISO.toLowerCase();
			});
			if (this.selectedCountry) {
				if (this.phoneNumber) {
					this.onPhoneNumberChange();
				} else {
					// Reason: avoid https://stackoverflow.com/a/54358133/1617590
					// tslint:disable-next-line: no-null-keyword
					// @ts-ignore
          this.propagateChange(null);
				}
			}
		}
	}
}
